import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ChevronDown, PenLine, Eraser, Search, User } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Likert order matches the CARF template: SD, D, Agree, Strongly Agree,
// Undecided (rightmost). Values 1-2-4-5 are the scored options; value 3 is
// reserved for Undecided so analytics can average scored responses only.
const LIKERT = [
  [1, "Strongly Disagree"],
  [2, "Disagree"],
  [4, "Agree"],
  [5, "Strongly Agree"],
  [3, "Undecided"],
];

function InfoField({ f, value, onChange, token }) {
  if (f.kind === "select") {
    return (
      <div className="relative">
        <select data-testid={`survey-field-${f.key}`} value={value || ""} onChange={(e) => onChange(e.target.value)}
          className="h-12 w-full appearance-none rounded-xl border border-[#E5DFD3] bg-white px-4 pr-10 text-sm text-[#3E2723] focus:border-[#8D6E63] focus:outline-none focus:ring-1 focus:ring-[#8D6E63]">
          <option value="">Enter here</option>
          {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <ChevronDown size={16} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[#7B6157]" />
      </div>
    );
  }
  if (f.kind === "client_select") return <ClientTypeahead token={token} value={value} onChange={onChange} testId={`survey-field-${f.key}`} />;
  return (
    <input data-testid={`survey-field-${f.key}`} value={value || ""} onChange={(e) => onChange(e.target.value)}
      placeholder="Enter here"
      className="h-12 w-full rounded-xl border border-[#E5DFD3] bg-white px-4 text-sm text-[#3E2723] focus:border-[#8D6E63] focus:outline-none focus:ring-1 focus:ring-[#8D6E63]" />
  );
}

// Client typeahead. Types a name, calls /public/surveys/{token}/clients?q=..
// after a small debounce, shows matching clients in a dropdown. On pick, saves
// the client id to the answer and shows the display name in the input.
function ClientTypeahead({ token, value, onChange, testId }) {
  const [text, setText] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const boxRef = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  useEffect(() => {
    if (picked || !text.trim() || text.length < 2) { setResults([]); return; }
    const h = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/public/surveys/${token}/clients`, { params: { q: text.trim() } });
        setResults(r.data || []); setOpen(true);
      } catch { setResults([]); }
    }, 250);
    return () => clearTimeout(h);
  }, [text, token, picked]);
  const pick = (c) => {
    setPicked(c);
    setText(`${c.first_name} ${c.last_name}`.trim());
    onChange(c.id);
    setOpen(false);
  };
  const clear = () => { setPicked(null); setText(""); onChange(""); setResults([]); };
  useEffect(() => { if (!value) { setPicked(null); setText(""); } }, [value]);
  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7B6157]" />
        <input data-testid={testId} value={text}
          onChange={(e) => { setText(e.target.value); if (picked) { setPicked(null); onChange(""); } }}
          onFocus={() => { if (results.length) setOpen(true); }}
          placeholder="Type a client's name..."
          className="h-12 w-full rounded-xl border border-[#E5DFD3] bg-white pl-10 pr-10 text-sm text-[#3E2723] focus:border-[#8D6E63] focus:outline-none focus:ring-1 focus:ring-[#8D6E63]" />
        {picked && (
          <button type="button" onClick={clear} data-testid={`${testId}-clear`}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-[#7B6157] hover:bg-[#F5F2EB]">×</button>
        )}
      </div>
      {picked && (
        <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-[#F5F2EB] px-2.5 py-1 text-[11px] font-semibold text-[#4E342E]">
          <User size={11} /> {picked.first_name} {picked.last_name}
          {picked.mrn && <span className="text-[#7B6157]">, {picked.mrn}</span>}
        </div>
      )}
      {open && results.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-60 overflow-y-auto rounded-xl border border-[#E5DFD3] bg-white py-1 shadow-lg">
          {results.map((c) => (
            <button key={c.id} type="button" onClick={() => pick(c)}
              data-testid={`survey-client-option-${c.id}`}
              className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-sm text-[#3E2723] hover:bg-[#FDFCF8]">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F5F2EB] text-[10px] font-bold text-[#4E342E]">
                {(c.first_name || "?")[0]}{(c.last_name || "?")[0]}
              </span>
              <span className="flex-1 truncate">{c.first_name} {c.last_name}</span>
              {c.mrn && <span className="text-xs text-[#7B6157]">{c.mrn}</span>}
            </button>
          ))}
        </div>
      )}
      {open && text.trim().length >= 2 && results.length === 0 && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 rounded-xl border border-[#E5DFD3] bg-white p-3 text-xs text-[#7B6157] shadow-lg">
          No clients match "{text.trim()}"
        </div>
      )}
    </div>
  );
}

// Inline signature pad. Kept local so the public survey shell does not depend
// on the authenticated api client.
function InlineSignaturePad({ value, onChange }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, c.width, c.height);
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, c.width, c.height);
      img.src = value;
    }
  }, [value]);
  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (canvasRef.current.width / rect.width),
             y: (t.clientY - rect.top) * (canvasRef.current.height / rect.height) };
  };
  const start = (e) => { e.preventDefault(); setDrawing(true); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.strokeStyle = "#3E2723"; ctx.lineWidth = 2; ctx.lineCap = "round"; };
  const move = (e) => { if (!drawing) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = () => { if (!drawing) return; setDrawing(false); onChange(canvasRef.current.toDataURL("image/png")); };
  const clear = () => { const c = canvasRef.current; const ctx = c.getContext("2d"); ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0, 0, c.width, c.height); onChange(""); };
  return (
    <div data-testid="survey-signature-pad">
      <canvas ref={canvasRef} width={800} height={200}
        className="w-full rounded-2xl border-2 border-dashed border-[#D7CCC8] bg-white touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <div className="mt-2 flex items-center justify-between text-xs text-[#7B6157]">
        <span className="flex items-center gap-1.5"><PenLine size={12} /> Draw your signature above</span>
        <button type="button" onClick={clear} className="flex items-center gap-1 rounded-full px-2.5 py-1 hover:bg-[#F5F2EB]"><Eraser size={12} /> Clear</button>
      </div>
    </div>
  );
}

function LikertRow({ qKey, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-x-8 gap-y-3">
      {LIKERT.map(([v, l]) => (
        <label key={v} className="inline-flex cursor-pointer items-center gap-2.5 text-[13px] text-[#3E2723] transition-colors hover:text-[#4E342E]">
          <input type="radio" name={qKey} value={v} checked={value === v}
            data-testid={`survey-${qKey}-${v}`} onChange={() => onChange(v)}
            className="h-[18px] w-[18px] cursor-pointer rounded-md border-2 border-[#D7CCC8] accent-[#4E342E]" />
          <span>{l}</span>
        </label>
      ))}
    </div>
  );
}

function YesNoRow({ qKey, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-3">
      {["Yes", "No"].map((v) => (
        <button key={v} type="button" data-testid={`survey-${qKey}-${v.toLowerCase()}`} onClick={() => onChange(v)}
          className={`rounded-full px-7 py-2 text-sm font-semibold transition-colors ${value === v ? "bg-[#4E342E] text-white shadow-sm" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>{v}</button>
      ))}
    </div>
  );
}

function NpsRow({ qKey, value, onChange }) {
  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {Array.from({ length: 11 }, (_, v) => (
          <button key={v} type="button" data-testid={`survey-${qKey}-${v}`} onClick={() => onChange(v)}
            className={`h-10 w-10 rounded-lg text-sm font-bold transition-colors ${value === v ? "bg-[#4E342E] text-white shadow-sm" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>{v}</button>
        ))}
      </div>
      <div className="mt-2 flex w-full justify-between text-[10px] text-[#84675B]"><span>Not at all likely</span><span>Extremely likely</span></div>
    </div>
  );
}

export default function SurveyFill() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({});
  const [signature, setSignature] = useState("");
  const [signerName, setSignerName] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/surveys/${token}`)
      .then((r) => { setData(r.data); if (r.data.completed) setDone(true); })
      .catch((e) => setError(e.response?.data?.detail || "Survey not found"));
  }, [token]);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/surveys/${token}`, {
        answers, signature: signature ? { image: signature, name: signerName, signed_at: new Date().toISOString() } : null,
      });
      setDone(true);
    } catch (e) { setError(e.response?.data?.detail || "Could not submit"); }
    finally { setBusy(false); }
  };

  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const s = data && data.survey;
  const { answered, required } = useMemo(() => {
    if (!s) return { answered: 0, required: 0 };
    const qs = (s.sections || []).flatMap((sec) => sec.questions || []).filter((q) => q.type !== "text");
    return { answered: qs.filter((q) => answers[q.key] !== undefined && answers[q.key] !== "").length, required: qs.length };
  }, [s, answers]);

  if (error && !data) return <Shell><p className="text-center font-semibold text-[#C62828]" data-testid="survey-error">{error}</p></Shell>;
  if (!data) return <Shell><p className="text-center text-[#84675B]">Loading survey...</p></Shell>;
  if (done) return (
    <Shell clinic={data.clinic_name}>
      <div className="rounded-3xl border border-[#EFEBE0] bg-white px-10 py-16 text-center shadow-sm" data-testid="survey-thankyou">
        <CheckCircle2 size={56} className="mx-auto text-[#2E7D32]" />
        <h2 className="mt-5 font-heading text-2xl font-bold text-[#3E2723]">Thank you!</h2>
        <p className="mt-2 text-sm text-[#7B6157]">Your feedback was submitted, and helps us improve our services.</p>
      </div>
    </Shell>
  );

  return (
    <Shell clinic={data.clinic_name}>
      <div className="rounded-3xl border border-[#EFEBE0] bg-white shadow-sm">
        <div className="border-b border-[#EFEBE0] px-8 py-10 text-center sm:px-14">
          <h1 className="font-heading text-3xl font-bold tracking-tight text-[#3E2723] sm:text-[32px]" data-testid="survey-title">{s.name}</h1>
          {(s.description || data.recipient_name) && (
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-[#7B6157]">
              {s.description}{data.recipient_name ? ` for ${data.recipient_name}` : ""}
            </p>
          )}
        </div>
        <div className="space-y-12 px-6 py-10 sm:px-14 sm:py-12">
          {(s.sections || []).map((sec) => (
            <section key={`${sec.letter}${sec.title}`} data-testid={`survey-section-${sec.letter}`}>
              <h2 className="mb-6 font-heading text-[16px] font-bold text-[#3E2723]">{sec.letter}. {sec.title}</h2>
              {sec.fields && (
                <div className={`grid gap-x-6 gap-y-5 ${sec.fields.length >= 4 ? "sm:grid-cols-2 lg:grid-cols-4" : sec.fields.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
                  {sec.fields.map((f) => (
                    <div key={f.key}>
                      <label className="mb-2 block text-xs font-medium text-[#5D4037]">{f.label}</label>
                      <InfoField f={f} value={answers[f.key]} onChange={(v) => set(f.key, v)} token={token} />
                    </div>
                  ))}
                </div>
              )}
              {sec.questions && (
                <div className="space-y-7">
                  {sec.questions.map((q, i) => (
                    <div key={q.key} data-testid={`survey-q-${q.key}`}>
                      <div className="mb-3 text-[14px] leading-relaxed text-[#3E2723]">{i + 1}. {q.text}</div>
                      {q.type === "likert" && <LikertRow qKey={q.key} value={answers[q.key]} onChange={(v) => set(q.key, v)} />}
                      {q.type === "yesno" && <YesNoRow qKey={q.key} value={answers[q.key]} onChange={(v) => set(q.key, v)} />}
                      {q.type === "nps" && <NpsRow qKey={q.key} value={answers[q.key]} onChange={(v) => set(q.key, v)} />}
                      {q.type === "text" && (
                        <textarea data-testid={`survey-${q.key}-text`} rows={3} value={answers[q.key] || ""} onChange={(e) => set(q.key, e.target.value)}
                          placeholder="Enter here"
                          className="w-full rounded-xl border border-[#E5DFD3] p-4 text-sm text-[#3E2723] focus:border-[#8D6E63] focus:outline-none focus:ring-1 focus:ring-[#8D6E63]" />
                      )}
                    </div>
                  ))}
                </div>
              )}
              {sec.signature && (
                <div className="mt-8">
                  <label className="mb-2 block text-[13px] font-medium text-[#3E2723]">Electronic Signature</label>
                  <div className="mb-4">
                    <input value={signerName} onChange={(e) => setSignerName(e.target.value)}
                      placeholder="Type your name"
                      data-testid="survey-signer-name"
                      className="h-11 w-full rounded-xl border border-[#E5DFD3] bg-white px-4 text-sm focus:border-[#8D6E63] focus:outline-none focus:ring-1 focus:ring-[#8D6E63] sm:max-w-sm" />
                  </div>
                  <InlineSignaturePad value={signature} onChange={setSignature} />
                </div>
              )}
            </section>
          ))}
        </div>
      </div>
      <div className="sticky bottom-4 mt-8 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#3E2723] px-6 py-3.5 text-white shadow-xl">
        <span className="text-xs">{answered} of {required} questions answered</span>
        <button data-testid="survey-submit-btn" onClick={submit} disabled={busy || answered === 0}
          className="rounded-full bg-white px-7 py-2.5 text-sm font-bold text-[#3E2723] transition-colors hover:bg-[#F5F2EB] disabled:opacity-50">
          {busy ? "Submitting..." : "Submit Survey"}
        </button>
      </div>
    </Shell>
  );
}

function Shell({ children, clinic }) {
  return (
    <div className="min-h-screen bg-[#F7F4EC] px-4 py-10 sm:px-6 sm:py-12">
      <div className="mx-auto max-w-4xl">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <img src="/logos/cocoa_ehr_logo_color.webp" alt="Cocoa EHR" className="h-14 w-auto" data-testid="survey-logo" />
          {clinic && <div className="text-sm font-semibold text-[#4E342E]">{clinic}</div>}
          <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#84675B]">Quality and Outcomes Survey</div>
        </div>
        {children}
        <p className="mt-10 text-center text-[10px] text-[#84675B]">Powered by Cocoa EHR, responses are confidential</p>
      </div>
    </div>
  );
}
